import { useCallback, useEffect, useRef, useState } from 'react';
import { msalInstance } from '@/config/msalConfig';

const IDLE_WARNING_MS = 20 * 60 * 1000; // 20 min idle → show warning modal
const IDLE_LOGOUT_S   =  5 * 60;        // 5 min (seconds) — countdown display + auto-logout duration

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'] as const;

export function useIdleTimeout() {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(IDLE_LOGOUT_S);
  const isWarningRef    = useRef(false);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef    = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current)  clearTimeout(logoutTimerRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current  = null;
  }, []);

  const startIdleCycle = useCallback(() => {
    clearAllTimers();
    warningTimerRef.current = setTimeout(() => {
      isWarningRef.current = true;
      setShowWarning(true);
      logoutTimerRef.current = setTimeout(() => {
        void msalInstance.logoutRedirect();
      }, IDLE_LOGOUT_S * 1000);
    }, IDLE_WARNING_MS);
  }, [clearAllTimers]);

  // Countdown ticker in its own effect — avoids deep function nesting.
  useEffect(() => {
    if (!showWarning) {
      setSecondsRemaining(IDLE_LOGOUT_S);
      return;
    }
    setSecondsRemaining(IDLE_LOGOUT_S);
    countdownRef.current = setInterval(() => {
      setSecondsRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      countdownRef.current = null;
    };
  }, [showWarning]);

  // Activity events only reset the timer when the warning is NOT visible.
  const resetTimer = useCallback(() => {
    if (isWarningRef.current) return;
    startIdleCycle();
  }, [startIdleCycle]);

  const stayLoggedIn = useCallback(() => {
    clearAllTimers();
    isWarningRef.current = false;
    setShowWarning(false);
    startIdleCycle();
  }, [clearAllTimers, startIdleCycle]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') resetTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    startIdleCycle();

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearAllTimers, resetTimer, startIdleCycle]);

  return { showWarning, secondsRemaining, stayLoggedIn };
}
